// frontend/src/components/grid/DetailGrid.jsx
//
// The sub-grid of a master-detail row, rendered through a custom
// `detailCellRenderer` (NOT native `detailGridOptions`, which cannot carry a
// header or footer).
//
// The markup is what AG Grid's own detail renderer emits — DetailRow's
// `.ag-details-row` around an `.ag-details-grid` — so the grid's detail
// stylesheet and the documented `.ag-details-grid` variable overrides
// (styles/pages.css, MASTER-DETAIL ROW STYLING) apply to it unchanged.
//
// The detail grid is its own <AgGridReact> instance, with the shared theme,
// column types, grid state and sizing wired the same way as every top-level
// grid. Callers pass only what varies — `rowData`, `columnDefs`, `stateKey` —
// plus an optional `header` / `footer` and any extra grid options (editable
// handlers, `context`, `sideBar`) which pass straight through.
//
// Sizing comes with detailGridDefaults (gridDefaults, "Column sizing"): AG
// Grid measures every column to its content and stretches them over the width
// the master row leaves, master row resizes included. The caller's columns
// carry no `flexColumn()`.

import { AgGridReact } from 'ag-grid-react';
import DetailRow from './DetailRow';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useGridState } from '../../hooks/useGridState';
import {
  defaultColDef as sharedDefaultColDef,
  detailGridDefaults,
  copyOnlyContextMenuItems,
} from '../../utils/gridDefaults';

export default function DetailGrid({
  rowData,
  columnDefs,
  stateKey,
  defaultHidden,
  defaultColDef = sharedDefaultColDef,
  header = null,
  footer = null,
  ...gridProps
}) {
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridState = useGridState(stateKey, defaultHidden);

  return (
    <DetailRow>
      {header}
      <div className="ag-details-grid">
        <AgGridReact
          theme={gridTheme}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          columnTypes={columnTypes}
          getContextMenuItems={copyOnlyContextMenuItems}
          domLayout="autoHeight"
          {...gridState}
          suppressColumnVirtualisation={true}
          {...detailGridDefaults}
          {...gridProps}
        />
      </div>
      {footer}
    </DetailRow>
  );
}
